import type { ReactNode } from 'react';
import {
  Button,
  InlineNotification,
  NumberInput,
  Tag,
  TextArea,
  TextInput,
  Toggle,
} from '@carbon/react';
import { WarningAltFilled } from '@carbon/react/icons';
import { CountyTree } from './county-tree';
import { useCounties } from './hooks';
import { SuffixEditor } from './suffix-editor';
import {
  numberOf,
  parseList,
  THRESHOLD_MAX,
  thresholdValidity,
  type FormState,
  type ThresholdField,
} from './utils';
import type { AutoMatchConfiguration, CountyOption } from './types';
import styles from './page.module.scss';

interface FormSectionProps {
  form: FormState;
  patch: (changes: Partial<FormState>) => void;
}

interface ConfigSectionProps {
  title: string;
  description: string;
  children: ReactNode;
}

/**
 * A titled block of the configuration form.
 * @param {ConfigSectionProps} props - Its title, description and fields
 * @return {JSX.Element} The section
 */
function ConfigSection({ title, description, children }: ConfigSectionProps) {
  return (
    <section className={styles.section}>
      <div>
        <h2 className={styles.sectionTitle}>{title}</h2>
        <p className={styles.sectionDescription}>{description}</p>
      </div>
      <div className={styles.sectionBody}>{children}</div>
    </section>
  );
}

const NO_COUNTIES: CountyOption[] = [];

/**
 * The master switch and the optional county filter.
 * @param {FormSectionProps} props - The form and its patcher
 * @return {JSX.Element} The section
 */
export function StatusSection({ form, patch }: FormSectionProps) {
  const { data: counties = NO_COUNTIES, isError, isSuccess } = useCounties();

  return (
    <ConfigSection
      title="Status & rollout"
      description="The master switch and an optional county filter. With the
            switch on and no counties selected, auto-match runs everywhere (100%
            rollout); add counties to restrict it to just those. When the switch
            is off, decisions are still recorded — as suppressions."
    >
      <Toggle
        id="auto-match-enabled"
        labelText="Auto-match"
        labelA="Off — every decision suppressed"
        labelB="On"
        toggled={form.enabled}
        onToggle={(checked: boolean) => patch({ enabled: checked })}
      />
      {form.enabled && form.enabledCountyIds.length === 0 && (
        <InlineNotification
          kind="info"
          lowContrast
          hideCloseButton
          title="Running in all counties (100% rollout)"
          subtitle="No county filter is set, so auto-match runs everywhere it's otherwise eligible. Select counties below to restrict it."
        />
      )}
      {isError && (
        <InlineNotification
          kind="warning"
          lowContrast
          hideCloseButton
          title="County list unavailable"
          subtitle="Existing selections are preserved, but counties cannot be changed until the list loads."
        />
      )}
      <CountyTree
        counties={counties}
        selectedIds={form.enabledCountyIds}
        onChange={(ids) => patch({ enabledCountyIds: ids })}
        disabled={!isSuccess}
      />
    </ConfigSection>
  );
}

interface ThresholdInputProps extends FormSectionProps {
  field: ThresholdField;
  id: string;
  label: string;
  helperText: string;
  valid: boolean;
}

/**
 * One whole-number threshold, bounded by THRESHOLD_MAX.
 * @param {ThresholdInputProps} props - The field, its labels and the form
 * @return {JSX.Element} The input
 */
function ThresholdInput({
  form,
  patch,
  field,
  id,
  label,
  helperText,
  valid,
}: ThresholdInputProps) {
  const value = form[field];
  return (
    <NumberInput
      id={id}
      label={label}
      helperText={helperText}
      min={0}
      max={THRESHOLD_MAX[field]}
      step={1}
      allowEmpty
      value={Number.isNaN(value) ? '' : value}
      invalid={!valid}
      invalidText={`Must be a whole number between 0 and ${THRESHOLD_MAX[field]}`}
      onChange={(_e, { value: next }) => patch({ [field]: numberOf(next) })}
    />
  );
}

interface ThresholdsSectionProps extends FormSectionProps {
  candidateCap: number;
}

/**
 * The score bar a match must clear, with the read-only candidate cap.
 * @param {ThresholdsSectionProps} props - The form, its patcher and the cap
 * @return {JSX.Element} The section
 */
export function ThresholdsSection({
  form,
  patch,
  candidateCap,
}: ThresholdsSectionProps) {
  const valid = thresholdValidity(form);

  return (
    <ConfigSection
      title="Thresholds"
      description="The score bar a match must clear. T is the minimum
            composite score; a briefed matcher run clears the LLM confidence
            threshold instead. Δ is how far the winner must separate from the
            runner-up, in points of LLM confidence on a briefed run. The
            candidate cap (G7) mirrors the entity-match agent's max-suggestions
            setting and is shown here for reference only — it isn't editable on
            this screen."
    >
      <div className={styles.thresholdGrid}>
        <ThresholdInput
          form={form}
          patch={patch}
          field="thresholdT"
          id="threshold-t"
          label="T — confidence threshold"
          helperText="Minimum composite score (0–100)"
          valid={valid.thresholdT}
        />
        <ThresholdInput
          form={form}
          patch={patch}
          field="llmConfidenceThreshold"
          id="llm-confidence-threshold"
          label="LLM confidence threshold"
          helperText="Briefed matcher runs: the LLM's confidence, as a percentage, that stands in for the score threshold"
          valid={valid.llmConfidenceThreshold}
        />
        <ThresholdInput
          form={form}
          patch={patch}
          field="thresholdDelta"
          id="threshold-delta"
          label="Δ — separation margin"
          helperText="Winner must lead by this much (0–30); briefed matcher runs: points of LLM confidence"
          valid={valid.thresholdDelta}
        />
        <TextInput
          id="candidate-cap"
          labelText="Candidate cap (G7)"
          helperText="Mirrors the entity-match agent's max-suggestions setting"
          value={String(candidateCap)}
          readOnly
        />
      </div>
      <Toggle
        id="info-suppresses"
        labelText="Treat info-severity flags as suppressors"
        labelA="Off — only warning flags suppress"
        labelB="On — info flags also suppress"
        toggled={form.infoSuppresses}
        onToggle={(checked: boolean) => patch({ infoSuppresses: checked })}
      />
    </ConfigSection>
  );
}

interface GuardrailsSectionProps extends FormSectionProps {
  guardrails: AutoMatchConfiguration['availableGuardrails'];
}

/**
 * The conjunctive gate's per-guardrail toggles.
 * @param {GuardrailsSectionProps} props - The form, its patcher and the roster
 * @return {JSX.Element} The section
 */
export function GuardrailsSection({
  form,
  patch,
  guardrails,
}: GuardrailsSectionProps) {
  return (
    <ConfigSection
      title="Guardrails"
      description="The conjunctive gate: every enabled guardrail must pass
            before a match auto-confirms. Disabling one widens what can
            auto-confirm — each toggle here is a precision decision."
    >
      <div className={styles.guardrailList}>
        {guardrails.map((guardrail) => (
          <div key={guardrail.id} className={styles.guardrailRow}>
            <div>
              <h3 className={styles.guardrailName}>
                {guardrail.id} — {guardrail.name}
              </h3>
              <p className={styles.guardrailDescription}>
                {guardrail.description}
              </p>
              {form.guardrailsEnabled[guardrail.id] === false &&
                guardrail.warning && (
                  <div className={styles.guardrailWarning}>
                    <WarningAltFilled size={16} />
                    {guardrail.warning}
                  </div>
                )}
            </div>
            <div className={styles.guardrailToggle}>
              <Toggle
                id={`guardrail-${guardrail.id}`}
                size="sm"
                aria-label={`${guardrail.id} — ${guardrail.name}`}
                labelA="Off"
                labelB="On"
                toggled={form.guardrailsEnabled[guardrail.id] !== false}
                onToggle={(checked: boolean) =>
                  patch({
                    guardrailsEnabled: {
                      ...form.guardrailsEnabled,
                      [guardrail.id]: checked,
                    },
                  })
                }
              />
            </div>
          </div>
        ))}
      </div>
    </ConfigSection>
  );
}

/**
 * The gate's vocabulary lists and the entity-form suffix groups.
 * @param {FormSectionProps} props - The form and its patcher
 * @return {JSX.Element} The two sections
 */
export function VocabularySections({ form, patch }: FormSectionProps) {
  return (
    <>
      <ConfigSection
        title="Name lists"
        description="The vocabulary the gate reasons with, comma-separated.
            Organization markers make G1 read a name as an organization;
            never-auto phrases make G5 hold a party for review no matter how
            well it scores — matched against both the party name and its extracted
            role, so a common role word (e.g. “trustee”) also holds every name
            containing it."
      >
        <TextArea
          id="organizational-tokens"
          labelText={`Organization markers (G1) — ${
            parseList(form.organizationalTokensText).length
          } entries`}
          helperText="Comma-separated. A name containing one of these tokens is treated as an organization."
          rows={3}
          value={form.organizationalTokensText}
          onChange={(event) =>
            patch({ organizationalTokensText: event.target.value })
          }
        />
        <TextArea
          id="never-auto-phrases"
          labelText={`Never-auto phrases (G5) — ${
            parseList(form.neverAutoPhrasesText).length
          } entries`}
          helperText="Comma-separated. A party whose name or role contains one of these never auto-confirms. Matching ignores case, periods and extra spaces."
          rows={4}
          value={form.neverAutoPhrasesText}
          onChange={(event) =>
            patch({ neverAutoPhrasesText: event.target.value })
          }
        />
      </ConfigSection>

      <ConfigSection
        title="Entity form suffixes"
        description="Alias-equivalent legal-form suffixes: names differing
            only by a listed variant of the same canonical suffix are treated as
            the same form. Compatibility decides whether G1 suppresses when the
            staged name carries the suffix but the candidate is a different kind
            of organization."
      >
        <SuffixEditor
          suffixes={form.entityFormSuffixes}
          onChange={(entityFormSuffixes) => patch({ entityFormSuffixes })}
        />
      </ConfigSection>
    </>
  );
}

interface ChangeBarProps extends FormSectionProps {
  dirty: boolean;
  saving: boolean;
  canSave: boolean;
  onDiscard: () => void;
  onSave: () => void;
}

/**
 * The sticky bar: dirty state, the required rationale, discard and save.
 * @param {ChangeBarProps} props - The form state and its actions
 * @return {JSX.Element} The bar
 */
export function ChangeBar({
  form,
  patch,
  dirty,
  saving,
  canSave,
  onDiscard,
  onSave,
}: ChangeBarProps) {
  return (
    <div className={styles.changeBar}>
      <div className={styles.changeBarStatus}>
        {dirty ? (
          <Tag type="blue" size="sm">
            Unsaved changes
          </Tag>
        ) : (
          <Tag type="gray" size="sm">
            No changes
          </Tag>
        )}
      </div>
      <TextInput
        className={styles.rationaleInput}
        id="rationale"
        labelText="Rationale (required to save)"
        hideLabel
        placeholder="Rationale — why this change? (required, recorded on the version)"
        value={form.rationale}
        onChange={(event) => patch({ rationale: event.target.value })}
        disabled={!dirty}
      />
      <div className={styles.changeBarActions}>
        <Button
          kind="secondary"
          disabled={!dirty || saving}
          onClick={onDiscard}
        >
          Discard changes
        </Button>
        <Button kind="primary" disabled={!canSave} onClick={onSave}>
          {saving ? 'Saving…' : 'Save configuration'}
        </Button>
      </div>
    </div>
  );
}
